import { useEffect } from "react"
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  type MetaFunction,
} from "react-router"

import type { Route } from "./+types/root"
import { PublicNav } from "~/components/layout/public-nav"
import { PublicFooter } from "~/components/layout/public-footer"
import { localBusinessSchema } from "~/lib/seo"
import { SITE } from "~/data/site"
import "./app.css"
import "./marketing.css"

/*
 * -----------------------------------------------------------------------------
 * WHAT THIS FILE NO LONGER DOES
 * -----------------------------------------------------------------------------
 * On the platform, root.tsx carried the whole multi-tenant apparatus, because
 * one Worker served New Leaf's marketing pages AND every tenant's console:
 *
 *   - a `loader` that fetched /api/branding on every request to learn whose
 *     domain this was;
 *   - `allowMarketingTags`, gating New Leaf's Google Ads, Plausible, Clarity and
 *     Sprout tags to New Leaf's own hostnames — after those tags spent time
 *     firing conversions into New Leaf's Ads account from other tenants' traffic
 *     and session-recording other companies' admins into New Leaf's Clarity
 *     workspace;
 *   - `PRIVATE_PREFIXES`, so those tags never loaded on /admin, /team, /client;
 *   - `hideChrome`, so the public nav and footer did not render above a portal
 *     login form;
 *   - per-tenant theme <style> blocks and a per-tenant favicon.
 *
 * None of it applies here. This Worker serves one domain for one company, and
 * that domain has no private surfaces on it — the console lives at
 * nlc.broomstack.app, a different Worker. So every gate above is now
 * unconditionally true, and saying so in code is clearer than keeping branches
 * that can only take one path.
 *
 * The tags are still guarded by `import.meta.env.PROD`, which is the guard that
 * was always doing the real work for local development.
 */

/**
 * LocalBusiness + CleaningService JSON-LD on every page.
 * New Leaf's own address, reviews and social links — computed once, no I/O.
 */
const GLOBAL_BUSINESS_SCHEMA = localBusinessSchema()

export const meta: MetaFunction = () => {
  return [
    { charset: "utf-8" },
    { name: "viewport", content: "width=device-width, initial-scale=1" },
    { name: "robots", content: "index, follow" },
    { name: "apple-mobile-web-app-capable", content: "yes" },
    { name: "apple-mobile-web-app-status-bar-style", content: "default" },
    { name: "mobile-web-app-capable", content: "yes" },
  ]
}

/*
 * The favicon is declared here again, statically.
 *
 * It lived in `links` on the platform too, until that turned out to serve New
 * Leaf's leaf to every tenant's browser tab and to the operator console, and it
 * was moved into Layout so it could be resolved per request. Static is correct
 * again here for the same reason it was wrong there: there is only one company
 * on this Worker, and the icon is hers.
 */
export const links: Route.LinksFunction = () => [
  { rel: "icon", href: "/images/favicon-32x32.png" },
  // Site-wide so the feed is discoverable from every page, not only /blog. This tag is
  // how a reader, an aggregator or a browser extension finds the feed at all — without it
  // the file exists but nothing announces it.
  {
    rel: "alternate",
    type: "application/rss+xml",
    title: "New Leaf Cleaning — Cleaning, explained",
    href: "/blog/feed.xml",
  },
  { rel: "apple-touch-icon", href: "/images/apple-touch-icon.png", sizes: "180x180" },
  { rel: "manifest", href: "/manifest.webmanifest" },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    // Two families only: Bricolage Grotesque (display, H1/H2) + Inter (everything else).
    // Replaced Fraunces/Figtree 2026-09-10 — the soft optical serif was the look
    // Vikelas asked to get rid of. Bricolage has no true italic, so display
    // accents are set with color + weight, never `font-style: italic`.
    href: "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wdth,wght@12..96,75..100,400..800&family=Inter:wght@400..700&display=swap",
  },
]

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* New Leaf's green. The platform resolved this per tenant; here it is hers. */}
        <meta name="theme-color" content="#4A7C59" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content={SITE.name} />
        <meta name="mobile-web-app-capable" content="yes" />
        <Meta />
        <Links />
        {import.meta.env.PROD && (
          <script
            defer
            data-domain={SITE.domain}
            src="https://plausible.io/js/script.js"
          />
        )}
        {import.meta.env.PROD && (
          <>
            <script async src="https://www.googletagmanager.com/gtag/js?id=AW-18172244727" />
            <script dangerouslySetInnerHTML={{ __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','AW-18172244727');` }} />
          </>
        )}
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        <script
          dangerouslySetInnerHTML={{
            __html: `if('serviceWorker' in navigator){window.addEventListener('load',function(){navigator.serviceWorker.register('/sw.js')})}`,
          }}
        />
        {/* Sprout — New Leaf's AI assistant, hosted on its own Worker. */}
        {import.meta.env.PROD && (
          <script
            defer
            src="https://nlc-sprout-chatbot.tekavraindustries.workers.dev/widget.js"
          />
        )}
      </body>
    </html>
  )
}

export default function App() {
  /*
   * Microsoft Clarity session recording.
   *
   * Unconditional here because every page on this Worker is a public marketing
   * page belonging to New Leaf. The gate it used to carry existed to keep it off
   * OTHER companies' admin consoles, where it was recording their staff working
   * with their own clients' names, addresses and invoices. There is no admin
   * console on this domain to record.
   */
  useEffect(() => {
    if (import.meta.env.PROD) {
      import("@microsoft/clarity").then(({ default: Clarity }) => {
        Clarity.init("ws0uhf5tj5")
      })
    }
  }, [])

  return (
    <>
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      <PublicNav />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(GLOBAL_BUSINESS_SCHEMA),
        }}
      />
      <Outlet />
      <PublicFooter />
    </>
  )
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!"
  let details = "An unexpected error occurred."
  let stack: string | undefined

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error"
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message
    stack = error.stack
  }

  return (
    <section className="error-section" id="main-content">
      <div className="container text-center">
        <h1 className="error-heading">{message}</h1>
        <p className="section-intro">{details}</p>
        {stack && <pre className="error-stack">{stack}</pre>}
        <a href="/" className="btn btn-primary mt-32">Back to Home</a>
      </div>
    </section>
  )
}
